// OrdersPage — Dark base44 design (matches funderprofutures.com prop-firm context)
// Shows challenge orders (Tier · Size · Platform · Status) and any wallet receipts.
// Layout matches the dark card from the base44 screenshot.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ID } from '@funderpro/shared';
import { API } from '../../lib/api';
import { getToken, getUser } from '../../lib/auth';
import { ChatBubble } from './ChatBubble';

type Tab = 'Receipts' | 'Challenges';

export function OrdersPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('Challenges');
  const [orders, setOrders] = useState<any[] | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const tok = getToken();
        const me = getUser();
        if (!tok || !me) { setOrders([]); return; }
        const data = await API.list<any>('Order', { user_id: me.id });
        setOrders(data);
      } catch (e) {
        setOrders([]);
      }
    })();
  }, []);

  if (orders === null) return <div className="p-4 text-tf-text-3">Loading…</div>;

  const filtered = orders.filter((o) => (tab === 'Receipts' ? o.kind === 'receipt' : o.kind === 'challenge'));

  return (
    <div className="relative min-h-screen bg-tf-surface-1 px-3 pt-3 pb-20">
      <div className="relative rounded-[24px] bg-[#0B0F1A] p-5 text-white shadow-[0_8px_30px_rgba(0,0,0,0.12)]" data-testid="orders-card">
        <h1 className="text-[26px] font-bold tracking-tight">Orders list</h1>

        {/* Segmented control */}
        <div className="mt-4 flex rounded-[14px] bg-[#161B27] p-1" data-testid="orders-tabs">
          {(['Receipts', 'Challenges'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`orders-tab-${t.toLowerCase()}`}
              className={['flex-1 rounded-[10px] py-2.5 text-[15px] font-semibold transition',
                t === tab ? 'bg-[#2A3142] text-white' : 'text-[#8E92A1]'].join(' ')}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Table header — challenge order fields */}
        <div className="mt-5 grid grid-cols-[1.4fr_0.8fr_1.2fr_0.8fr] gap-2 px-1 text-[12px] font-semibold text-[#5A6175]">
          <span>Challenge</span>
          <span>Size</span>
          <span>Platform</span>
          <span>Status</span>
        </div>
        <div className="mt-2 border-t border-[#1F2532]" />

        {/* List */}
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-[15px] text-[#5A6175]">No orders found</div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => nav(o.kind === 'challenge' ? '/portal/challenge/' + o.id : '/portal/orders/' + o.id)}
              data-testid={`order-row-${o.id}`}
              className="grid w-full grid-cols-[1.4fr_0.8fr_1.2fr_0.8fr] gap-2 border-b border-[#1F2532] px-1 py-3 text-left active:bg-[#161B27] last:border-b-0"
            >
              <span className="truncate text-[14px] font-medium text-white">
                {o.tier_name || o.product_name || o.kind} {o.size || (o.account_size ? `$${o.account_size / 1000}K` : '')}
              </span>
              <span className="truncate text-[13px] text-[#8E92A1]">${o.amount || 0}</span>
              <span className="truncate text-[13px] text-[#8E92A1]">{o.platform_display || o.platform || '—'}</span>
              <span className={['text-[13px] capitalize',
                o.status === 'paid' || o.status === 'active' || o.status === 'passed' ? 'text-tf-profit' :
                o.status === 'failed' ? 'text-tf-loss' : 'text-tf-warning'].join(' ')}>
                {o.status}
              </span>
            </button>
          ))
        )}

        <ChatBubble className="absolute -right-2 -bottom-2" />
      </div>
    </div>
  );
}
